import { Ref, watch } from 'vue';
import type { AggregateMediaController } from '../mediaControllerType';
import type CameraRegistrationStore from '../../../alignedView/CameraRegistrationStore';
import type AlignedViewStore from '../../../alignedView/AlignedViewStore';
import { localLinkedScale } from '../../../alignedView/homography';
import type { Point } from '../../../alignedView/homography';
import useLinkedViewers from './useLinkedViewers';

/**
 * Links pan/zoom recentering between the two cameras of the active registration
 * pair: panning or zooming one recenters the other on the same point, mapped
 * through the pair's fitted homography ({@link CameraRegistrationStore.linkedPoint}).
 * Active whenever the Camera Registration panel has a pair selected (the
 * panel clears the pair on close) and "Link pan/zoom" is on -- a file-loaded
 * transform drives it just as well as picked points, so it deliberately does
 * NOT require picking mode. The mapping assumes UNWARPED panes showing native
 * coordinates, so it stands down while the aligned view (which owns
 * navigation then, {@link useAlignedNavigation}) is active. Distinct from the general "sync cameras"
 * toggle (Controls.vue), which assumes identical pixel scale between panes.
 */
export default function useRegistrationNavigation(
  aggregateController: Ref<AggregateMediaController>,
  registration: CameraRegistrationStore,
  alignedView?: AlignedViewStore,
) {
  const {
    viewer, teardown, attach, guarded, applyView,
  } = useLinkedViewers(aggregateController);

  function link(camera: string, otherCamera: string) {
    return () => guarded(() => {
      // The homography mapping assumes unwarped panes; the aligned-view link
      // owns navigation while it is active.
      if (alignedView?.active.value) {
        return;
      }
      // Ignore the pan/zoom events onResize emits while resetting each pane to
      // its own native bounds, so one pane's reset isn't mapped onto its pair.
      if (aggregateController.value.resizing.value) {
        return;
      }
      const source = viewer(camera);
      const target = viewer(otherCamera);
      if (!source || !target) {
        return;
      }
      const center = source.center();
      const linked = registration.linkedPoint(camera, [center.x, center.y]);
      if (!linked || linked.camera !== otherCamera) {
        return;
      }
      // Match the visible extent: one source pixel spans `scale` target pixels
      // here (position-dependent for non-similarity fits, so sampled at center;
      // null when unavailable -- leave the target's zoom alone).
      const scale = localLinkedScale(
        (p) => registration.linkedPoint(camera, p)?.coord ?? null,
        [center.x, center.y],
      );
      applyView(target, {
        center: { x: linked.coord[0], y: linked.coord[1] },
        unitsPerPixel: scale === null ? null : source.unitsPerPixel(source.zoom()) * scale,
      });
    });
  }

  function setup() {
    teardown();
    const pair = registration.activePair.value;
    // Active while the panel has a pair and "Link pan/zoom" is on (once a
    // transform exists -- fitted or file-loaded -- linkedPoint returns
    // matches). attach() no-ops for a not-yet-ready
    // pane; the resizeTrigger watch re-runs setup once both viewers exist.
    if (registration.linkedNav.value && pair) {
      attach(pair.camA, link(pair.camA, pair.camB));
      attach(pair.camB, link(pair.camB, pair.camA));
      // Snap immediately from camA so toggling "Link pan/zoom" on (or a refit
      // under it) lines the pair up right away instead of waiting for the
      // first pan/zoom event. No-ops harmlessly while no transform exists yet
      // (linkedPoint returns null).
      link(pair.camA, pair.camB)();
    }
  }

  watch(
    [
      registration.linkedNav,
      registration.activePair,
      registration.homographies,
      aggregateController.value.resizeTrigger,
    ],
    setup,
    { deep: true },
  );

  /**
   * One-shot recenter (right-click while picking): center the clicked camera on
   * the clicked point and, when the pair has a fitted homography, the other
   * camera on the corresponding point. Guarded so it doesn't loop back through
   * the continuous link above.
   */
  function handleRecenterRequest(
    request: { camera: string; coord: Point; id: number } | null,
  ) {
    if (!request || alignedView?.active.value) {
      return;
    }
    const pair = registration.activePair.value;
    if (!pair || (request.camera !== pair.camA && request.camera !== pair.camB)) {
      return;
    }
    const source = viewer(request.camera);
    if (source) {
      guarded(() => source.center({ x: request.coord[0], y: request.coord[1] }));
    }
    const linked = registration.linkedPoint(request.camera, request.coord);
    const target = linked && viewer(linked.camera);
    if (linked && target) {
      guarded(() => target.center({ x: linked.coord[0], y: linked.coord[1] }));
    }
  }

  watch(() => registration.recenterRequest.value, handleRecenterRequest);
}
